import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";

import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { TestimonialCard } from "./components";
import { ArrowRight } from "./components/assets";

export const TestimonialImageStar: React.FC = () => {
    return (
        <div css={starStyles}>
            <Picture
                image={{
                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/3t76qr31nd0pyj71cs0562wxs6agxl02.webp",
                    alt: ""
                }}
            />
        </div>
    );
};

export const TestimonialSection = () => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const useGWP7617Gate = checkFeatureFlagGate(
        featureFlags.gwp7617JiraShortTermAiInfusionGate
    );

    const handleClick = () => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            attributes: {
                name: "atlas-55799-testimonial-button-clicked",
                section: "testimonial-section"
            }
        }).fire();
    };

    const header = intl.formatMessage({
        id: "jira-product-tour.testimonial-section.header-text",
        defaultMessage: "For teams big & small"
    });

    const subtitle = intl.formatMessage({
        id: "jira-product-tour.testimonial-section.subtitle-text",
        defaultMessage:
            "Hear from start-ups & large enterprises that prefer Atlassian"
    });

    const ctaTextOld = intl.formatMessage({
        id: "jira-product-tour.testimonial-section.cta-text",
        defaultMessage: "See more customer stories"
    });

    const ctaTextAi = intl.formatMessage({
        id: "jira-product-tour.testimonial-section.cta-text-ai",
        defaultMessage: "See how Roblox boosts collaboration with AI"
    });

    const ctaText = useGWP7617Gate ? ctaTextAi : ctaTextOld;

    return (
        <div css={sectionContainer}>
            <div css={testimonialContainer}>
                <div css={leftContainer}>
                    <TestimonialImageStar />
                    <div css={testimonialContentContainer}>
                        <div css={contentStyles}>
                            <h2 css={headingStyles}>{header}</h2>
                            <div css={subtitleStyles}>{subtitle}</div>
                            <div css={linkStyles}>
                                <a
                                    css={anchorStyles}
                                    href={useLocalizedHref("/customers/roblox")}
                                    onClick={handleClick}
                                >
                                    {ctaText}
                                    <ArrowRight />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <TestimonialCard
                    image="https://dam-cdn.atl.orangelogic.com/AssetLink/thb1c563p4m28u32ri7fe867jx24dxpl.webp"
                    quote={intl.formatMessage({
                        id: "jira-product-tour.testimonial-section.quote-text",
                        defaultMessage:
                            "Before, our team saw Atlassian as individual tools...Now, [features & integrations] like Jira macros & Smart Links have really been pivotal in collaboration, productivity, & discoverability."
                    })}
                    name={intl.formatMessage({
                        id: "jira-product-tour.testimonial-section.name-text",
                        defaultMessage: "Joe Cotant"
                    })}
                    title={intl.formatMessage({
                        id: "jira-product-tour.testimonial-section.job-title-text",
                        defaultMessage:
                            "Senior Technical Program Manager, Roblox"
                    })}
                />
            </div>
        </div>
    );
};

TestimonialSection.displayName = "TestimonialSection";

const sectionContainer = css({
    clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 88%)",
    background: wacToken("color.primary.black"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 0px 100px 0px",
    [media.above.lg]: {
        padding: "100px 0px 100px 0px"
    },
    [media.below.sm]: {
        padding: "10px 0px 200px 0px"
    }
});

const testimonialContainer = css({
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    [media.above.lg]: {
        gap: "150px"
    },
    [media.below.lg]: {
        flexDirection: "column",
        alignItems: "center",
        gap: "0px"
    }
});

const testimonialContentContainer = css({
    [media.below.lg]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    [media.below.sm]: {
        maxWidth: "396px",
        padding: "0 25px"
    }
});

const leftContainer = css({
    display: "flex",
    alignItems: "flex-start",
    gap: "10px"
});

const contentStyles = css({
    [media.above.lg]: {
        maxWidth: "270px"
    },
    [media.below.lg]: {
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        gap: "25px",
        marginBlockEnd: "56px"
    },
    [media.below.sm]: {
        maxWidth: "316px"
    }
});

const headingStyles = css({
    color: wacToken("color.neutral.light0"),
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: "700",
    lineHeight: "normal",
    margin: 0,
    [media.above.lg]: {
        fontSize: "40px",
        lineHeight: "48px"
    },
    [media.only.md]: {
        fontSize: "48px"
    },
    [media.only.sm]: {
        fontSize: "40px"
    },
    [media.below.sm]: {
        fontSize: "30px"
    }
});

const subtitleStyles = css({
    marginBlockStart: "4px",

    color: `var(--Neutrals-N60, ${wacToken("color.neutral.light400")})`,
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "24px"
});

const linkStyles = css({
    marginBlockStart: "32px",
    [media.below.lg]: {
        marginBlockStart: 0
    }
});

const anchorStyles = css({
    color: wacToken("color.extended.blue400")
});

const starStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "34px",
        height: "38px",
        [media.below.lg]: {
            display: "none"
        }
    }
});
